import { useState } from 'react'
import { useSelector } from 'react-redux'
import { DocumentDuplicateIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { EnvelopeIcon, ChevronRightIcon } from '@heroicons/react/24/solid'

import ScreenFrame from 'components/Frame/ScreenFrame'
import ContentFrame from 'components/Frame/ContentFrame'
import FrequentlyAskedQuestions from 'components/Support/FrequentlyAskesQuestions'

export default function CoachOnboardingOptionsView() {

    const onboardingOptions = [
        {
            icon:(
                <div className="h-12 w-12 rounded-full bg-orange-400 flex items-center justify-center">
                    <EnvelopeIcon className="h-8 w-8 text-white" />
                </div>
            ),
            title: 'Send Email Invite',
            description: 'Send an email invite with instructions on how to sign up and join your organization',
            action: () => navigate('/onboarding/coaches/invite'),
        }
    ]

    const faqs = [
        {
            question: "How does adding coaches to my organization work?",
            answer: [
                "When an organization is created a unique organization code is generated.",
                "This code can be used by other coaches to join your organization."
            ]
        },
        {
            question: "Where can I find my unique organization code?",
            answer: [
                "You can find your unique organization code at the top right of this page."
            ]
        },
        {
            question: "What is the difference between sending a coach an email invite versus just sending them the organization code?",
            answer: [
                "When you send an email invite, it includes detailed instructions on how to access the coach dashboard and get signed up. This invite email includes the organization code embedded.",
                "When you send just the organization code, it is up to you to further instruct the coach on how to sign up."
            ]
        },
        {
            question: "Can I preview the invite email before sending it?",
            answer: [
                "Yes. You can preview a plain text version of the invite email by selected the \"Send Email Invite\" option and clicking \"What if I want to send the email myself?\"",
                "This can be useful if you would like to send the more detailed setup instructions out yourself."
            ]
        },
        {
            question: "Can I post my organization code publicly?",
            answer: [
                "Yes you can. However be aware that anyone who comes across this code can use it to join your organization."
            ]
        },
        {
            question: "Can I change my organization code?",
            answer: [
                "Yes you can. Please reach out to support@kinesisintegrated.com to do so."
            ]
        },
        {
            question: "When a coach joins my organization, what permissions will they have?",
            answer: [
                "There are three different levels of permissions that a coach can have in your organization:",
                "1. Organization Owner. This is the coach that created the organiztion. They have full access to the organization.",
                "2. Organization Admin. This is a coach that has been given administrative access to the organization. They can manage the organization settings and view all athletes in an organization",
                "3. Coach. This is the base level of access. They can only view athletes that have been assigned to them.",
                "When a coach first joins your organization, they will be assigned the Coach role by default.",
                "To manage which role each coach has, go to the \"Coaches\" tab in the sidebar.",
                "To manage which athletes are assigned to which coaches, go to the \"Athletes\" tab in the sidebar."
            ]
        }   
    ]

    const navigate = useNavigate()

    const organizationCode = useSelector((state) => state.user.organizationCode)
    
    const [showCopiedText, setShowCopiedText] = useState(false)
    const [showOrganizationCodeTooltip, setShowOrganizationCodeTooltip] = useState(false)

    const CopyOrganizationCodeToClipboard = () => {
        navigator.clipboard.writeText(organizationCode);
        setShowCopiedText(true);
        setTimeout(() => setShowCopiedText(false), 1000);
    }

    return (
        <ScreenFrame>
            <div className='flex items-center space-y-1 gap-x-2 pb-2'>
                <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2 flex-grow">
                    How would you like to add coaches?
                </h1>

                <div 
                    className='relative cursor-pointer' 
                    onClick={CopyOrganizationCodeToClipboard}
                >
                    <ContentFrame>
                        <div className='py-2 pl-4 pr-10 text-sm text-center bg-white font-bold'>
                            {organizationCode}
                        </div>
                    </ContentFrame>

                    <div className='absolute inset-y-0 right-4 flex items-center z-20'>
                        <DocumentDuplicateIcon className='h-4 w-4 text-gray-600' />
                    </div>

                    {showCopiedText && (
                        <div className='absolute inset-y-0 w-[90%] flex items-center justify-center bg-white m-1 rounded-lg z-10'>
                            <p className='text-center text-sm font-bold text-gray-500'>Copied!</p>
                        </div>
                    )}
                </div>

                <div 
                    className='relative'
                    onMouseEnter={() => setShowOrganizationCodeTooltip(true)}
                    onMouseLeave={() => setShowOrganizationCodeTooltip(false)}
                >
                    <QuestionMarkCircleIcon className='h-5 w-5 text-gray-500' />

                    {showOrganizationCodeTooltip && (
                        <div className="absolute top-full left-1/2 mt-4 w-40 -translate-x-1/2 transform rounded-md bg-white p-4 text-xs shadow-xl z-30">
                            This is your unique organization code. You can share it with coaches to allow them to join your organization.
                        </div>
                    )}
                </div>
            </div>

            {onboardingOptions.map((option, index) => (
                <ContentFrame key={index}>
                    <div 
                        className="w-full flex items-center p-6 gap-x-6 cursor-pointer hover:bg-gray-50 transition-all"
                        onClick={option.action}
                    >
                        <div>
                            {option.icon}
                        </div>

                        <div className='flex-1 space-y-1'>
                            <div className="text-xl font-semibold">{option.title}</div>
                            <div className="text-sm text-gray-500">{option.description}</div>
                        </div>

                        <ChevronRightIcon className="h-6 w-6 text-gray-400 group-hover:text-orange-500 transition-colors" />
                    </div>
                </ContentFrame>
            ))}

            <div className='pt-4'>Frequently Asked Questions (FAQs)</div>

            <FrequentlyAskedQuestions faqs={faqs} />

            <p className='pt-4 text-center text-gray-600'>
                Have additional questions? Contact us at <a href="mailto:support@kinesisintegrated.com" className="text-blue-500 underline">support@kinesisintegrated.com</a>
            </p>
        </ScreenFrame>
    )
}
